import React, { useContext, useEffect, useState } from "react";
import { api, root } from "../../services/api";
import { setLanguageToI18n } from "../../services/i18n";
import { UserContext } from "../../UserProvider";
import "./styles.scss";
import { useParams } from "react-router-dom";
import { UTMContext } from "../../contexts/UTMContext";
import { PicPayCardFlow } from "./flows/PicPayCardFlow";
import { PixFlow } from "./flows/PixFlow";
import { PagSeguroFlow } from "./flows/PagSeguroFlow";
import { DefaultFlow } from "./flows/DefaultFlow";
import { MissingFields } from "./fields/MissingFields";
import { ProgressSpinner } from "primereact/progressspinner";

export function LastStep() {
  const { utm_source, utm_medium, utm_campaign } = useContext(UTMContext);
  const { server, gateway } = useParams();
  const [missing, setMissing] = useState(undefined);

  const { verifiedUsername } = useContext(UserContext);

  const startParams = JSON.parse(localStorage.getItem("startParams") || "{}");
  const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");

  const transactionData = {
    username: startParams.username || "",
    token: startParams.token || "",
    server,
    gateway,
    currencyCode: server === "globalusd" || server === "seasonusd" ? "USD" : "BRL",
    items: cartItems,
    utm_source,
    utm_medium,
    utm_campaign,
  };

  useEffect(() => {
    let getParams = localStorage.getItem("startParams");
    if (getParams) {
      if (JSON.parse(getParams).lang) {
        setLanguageToI18n(JSON.parse(getParams).lang);
      } else {
        setLanguageToI18n("en-US");
      }
    } else {
      setLanguageToI18n("en-US");
    }

    window.dispatchEvent(
      new CustomEvent("add-route", {
        detail: {
          label: "Confirmation",
          url: `/${server}/checkout/${gateway}`,
          level: 3,
        },
      })
    );

    verifyMissingFields();
  }, []);

  async function verifyMissingFields() {
    let response = await root.get("/data/config.json");
    let storeInfo = response.data[server];
    let getParams = JSON.parse(localStorage.getItem("startParams"));
    let { username, token } = getParams;

    const params = new URLSearchParams();
    params.append("operation", "isInformationComplete");
    params.append("username", username);
    params.append("token", token);
    // O backend passou a responder 401 sem corpo quando o token nao vale, em vez de 200 com
    // {status:"fail"} -- o corpo antigo revelava que o problema era o token. Sem este catch o
    // axios lancaria aqui e o callback nunca rodaria.
    let informationComplete;
    try {
      informationComplete = await api.post("payment/personal-info", params);
    } catch (error) {
      console.error("Falha ao verificar dados pessoais:", error);
      return;
    }

    personalInformationCallback(informationComplete, storeInfo.ignoreFields);
  }

  function personalInformationCallback(informationComplete, ignoreFields) {
    const { data, status } = informationComplete;

    // O PicPay-card exige nome, CPF e telefone do cliente. Esses campos não podem ser
    // suprimidos por `ignoreFields`, senão o usuário chega no fluxo de cartão com o
    // account_info incompleto e o PicPay recusa (400). Removemos esses campos do
    // ignore efetivo quando o gateway é picpay-card.
    const PICPAY_REQUIRED_FIELDS = ["cpf", "fullname", "phone"];
    let effectiveIgnore = ignoreFields || [];
    if (gateway === "picpay-card") {
      effectiveIgnore = effectiveIgnore.filter(
        (field) => !PICPAY_REQUIRED_FIELDS.includes(field)
      );
    }
    // A Xsolla não recebe o telefone e coleta o que precisa no próprio checkout; o backend
    // também deixou de exigi-lo nela (GetMissingPurchaseInfo). Pedir aqui só somava uma etapa
    // ao funil e travava quem tem um número recusado pela validação.
    if (gateway === "xsolla") {
      effectiveIgnore = [...effectiveIgnore, "phone"];
    }

    let filtered = [];
    if (data.missing) {
      filtered = data.missing.filter((value) => {
        return !effectiveIgnore.includes(value);
      });
    }

    if (status === 200) {
      if (data.isComplete || (filtered && filtered.length === 0)) {
        setMissing({});
        return 0;
      } else {
        if (data.message === "invalid token") {
          window.location = `/`;
          return 1;
        } else {
          setMissing(filtered);
          return 1;
        }
      }
    }
  }

  const renderContent = () => {
    if (missing === undefined) {
      return (
        <div className="spinner-div">
          <ProgressSpinner />
        </div>
      );
    }

    if (Object.keys(missing).length > 0) {
      return (
        <MissingFields
          missing={missing}
          setMissing={setMissing}
          transactionData={transactionData}
        />
      );
    }

    switch (true) {
      case gateway === "picpay-card":
        return (
          <PicPayCardFlow
            transactionData={transactionData}
            onRequirePersonalInfo={verifyMissingFields}
          />
        );
      case gateway === "iugu-pix" || gateway === "omie-pix" || gateway === "picpay-pix" || gateway === "picpay":
        return (
          <PixFlow
            transactionData={transactionData}
            onRequirePersonalInfo={verifyMissingFields}
          />
        );
      case gateway.startsWith("pagseguro"):
        return (
          <PagSeguroFlow
            transactionData={transactionData}
            onRequirePersonalInfo={verifyMissingFields}
          />
        );
      default:
        return (
          <DefaultFlow
            transactionData={transactionData}
            onRequirePersonalInfo={verifyMissingFields}
          />
        );
    }
  };

  return <>{verifiedUsername && renderContent()}</>;
}